import { Stack, styled } from '@mui/material'
import { CaretLeftIcon } from '@phosphor-icons/react'
import { FileUploadCard, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { TinyButton } from 'ComponentLibrary/src/piazza/buttons'
import type { ReactElement, ReactNode } from 'react'

export function FileImportStepCard({
    title,
    description,
    icon,
    iconBackgroundColor,
    uploadTitle,
    uploadDescription,
    file,
    setFile,
    disabled,
    backDisabled,
    errorMessage,
    allowedFileTypes,
    allowOverwrite = false,
    maxFileSizeBytes,
    fileSizeErrorMessage,
    options,
    beforeUpload,
    afterCard,
    children,
    backLabel = 'Andere Methode wählen',
    onBack,
}: {
    title: string
    description: string
    icon: ReactElement
    iconBackgroundColor: string
    uploadTitle: string
    uploadDescription?: string
    file?: File
    setFile: (file?: File) => void
    disabled: boolean
    backDisabled?: boolean
    errorMessage?: string
    allowedFileTypes: string[]
    allowOverwrite?: boolean
    maxFileSizeBytes?: number
    fileSizeErrorMessage?: string
    options?: Array<{ title: string; callback: () => void; danger?: boolean; icon?: ReactElement }>
    beforeUpload?: ReactNode
    afterCard?: ReactNode
    children?: ReactNode
    backLabel?: string
    onBack?: () => void
}) {
    return (
        <UploadStepWrapper>
            <UploadStepCard>
                <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center" width="100%">
                    <UploadStepIconContainer $backgroundColor={iconBackgroundColor}>{icon}</UploadStepIconContainer>
                    <Stack gap={tokens.SizingSpacing075} minWidth={0}>
                        <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                            {title}
                        </Typography>
                        <Typography variant="textXS" color={tokens.ColorTextDefault}>
                            {description}
                        </Typography>
                    </Stack>
                </Stack>
                {beforeUpload}
                <FileUploadCard
                    title={uploadTitle}
                    description={uploadDescription}
                    setFile={setFile}
                    file={file}
                    disabled={disabled}
                    errorMessage={errorMessage}
                    allowedFileTypes={allowedFileTypes}
                    allowOverwrite={allowOverwrite}
                    maxFileSizeBytes={maxFileSizeBytes}
                    fileSizeErrorMessage={fileSizeErrorMessage}
                    options={options}
                />
                {children}
            </UploadStepCard>
            {afterCard && (
                <Stack width="100%" maxWidth="600px">
                    {afterCard}
                </Stack>
            )}
            {onBack && <TinyButton label={backLabel} leftIcon={<CaretLeftIcon />} onClick={onBack} disabled={backDisabled ?? disabled} />}
        </UploadStepWrapper>
    )
}

const UploadStepWrapper = styled(Stack)(() => ({
    width: '100%',
    alignItems: 'center',
    gap: tokens.SizingSpacing150,
}))

const UploadStepCard = styled(Stack)(() => ({
    width: '100%',
    maxWidth: '600px',
    gap: tokens.SizingSpacing300,
    padding: tokens.SizingSpacing150,
    border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
    borderRadius: tokens.SizingCornerM,
    backgroundColor: tokens.ColorSurfaceDefault,
}))

const UploadStepIconContainer = styled(Stack, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $backgroundColor: string }>(({ $backgroundColor }) => ({
    width: '40px',
    height: '56px',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.SizingCornerS,
    backgroundColor: $backgroundColor,
    '& svg': {
        width: tokens.SizingIconM,
        height: tokens.SizingIconM,
    },
}))
